<template>
    <div class="media-library-container">
        <b-alert variant="danger" :show="noPermissions"> {{ $t("integrations.media.noPermissions") }} </b-alert>

        <!-- Empty State -->
        <div v-if="$isAdmin() && libraries && libraries.length < 1" class="empty-library-state">
            <div class="empty-icon">
                <b-icon icon="folder-plus" font-scale="3"></b-icon>
            </div>
            <h4>{{ $t("integrations.media.startByCreating") }}</h4>
            <b-button @click="createLibrary.show = true" variant="primary" size="lg" class="mt-3">
                <b-icon icon="plus-circle" class="mr-2"></b-icon>
                {{ $t('add') }}
            </b-button>
        </div>

        <div 
            v-if="libraries" 
            class="media-library-content"
            @dragenter.prevent="handleGlobalDragEnter"
            @dragleave.prevent="handleGlobalDragLeave"
            @dragover.prevent="handleGlobalDragOver"
            @drop.prevent="handleGlobalDrop"
        >
            <!-- Drag and Drop Overlay -->
            <div 
                v-if="isDragging && $can('upload protokol-media')"
                class="drop-overlay"
            >
                <div class="drop-overlay-content">
                    <b-icon icon="cloud-upload" font-scale="4" class="drop-icon"></b-icon>
                    <p class="drop-text">{{ $t('dragAndDropFiles') }}</p>
                </div>
            </div>

            <div v-if="currentLib">
                <!-- Modern Header -->
                <div class="media-header">
                    <div class="header-top">
                        <h3 class="library-title">
                            <b-icon icon="folder2-open" class="mr-2"></b-icon>
                            {{ $t('theFiles') }}
                        </h3>
                        <div class="header-actions">
                            <b-button 
                                variant="outline-secondary" 
                                size="sm" 
                                @click="$router.push('/integrations/page/protokol-media/documents')"
                                class="mr-2"
                            >
                                <b-icon icon="file-earmark-text" class="mr-1"></b-icon>
                                {{ $t('documents') }}
                            </b-button>
                            <b-button-group size="sm" class="view-toggle mr-3">
                                <b-button :variant="viewMode === 'grid' ? 'primary' : 'outline-secondary'" @click="viewMode = 'grid'">
                                    <b-icon icon="grid-3x3-gap"></b-icon>
                                </b-button>
                                <b-button :variant="viewMode === 'list' ? 'primary' : 'outline-secondary'" @click="viewMode = 'list'">
                                    <b-icon icon="list-ul"></b-icon>
                                </b-button>
                            </b-button-group>
                            <b-button variant="outline-primary" size="sm" @click="uploadModal.show = true" v-if="$can('upload protokol-media')">
                                <b-icon icon="cloud-upload" class="mr-1"></b-icon>
                                {{ $t('upload') }}
                            </b-button>
                        </div>
                    </div>

                    <!-- Search and Breadcrumb -->
                    <div class="header-search-breadcrumb">
                        <div class="breadcrumb-nav">
                            <b-button variant="link" @click="chdir('')" class="breadcrumb-item">
                                <b-icon icon="house-fill"></b-icon>
                            </b-button>
                            <template v-for="(dir, index) in dirPath">
                                <span v-if="dir" :key="'sep-' + index" class="breadcrumb-separator">/</span>
                                <b-button 
                                    v-if="dir"
                                    :key="dir" 
                                    variant="link" 
                                    @click="chdir(dir)"
                                    :class="['breadcrumb-item', { 'active': dir === dirPath[dirPath.length - 1] }]"
                                >
                                    {{ dir }}
                                </b-button>
                            </template>
                        </div>
                        <div class="search-box">
                            <b-icon icon="search" class="search-icon"></b-icon>
                            <b-form-input 
                                :placeholder="`${$t('search')}...`" 
                                @input="search"
                                class="search-input"
                            />
                        </div>
                    </div>
                </div>

                <b-spinner v-if="changingLib" class="loading-spinner" />

                <!-- Toolbar -->
                <div v-else class="media-toolbar">
                    <div class="toolbar-left">
                        <span v-if="itemsSelected.length > 0" class="selection-count">
                            {{ itemsSelected.length }} {{ $t('selected') }}
                        </span>
                    </div>
                    <div class="toolbar-right">
                        <template v-if="itemsSelected.length > 0">
                            <b-button 
                                variant="outline-success" 
                                size="sm" 
                                @click="downloadFile"
                                v-if="itemsSelected.length == 1 && $can('download protokol-media')"
                                class="mr-2"
                            >
                                <b-icon icon="download"></b-icon>
                                {{ $t('download') }}
                            </b-button>
                            <b-button 
                                variant="outline-danger" 
                                size="sm" 
                                @click="deleteFile"
                                v-if="$can('delete protokol-media')"
                            >
                                <b-icon icon="trash"></b-icon>
                                {{ $t('delete') }}
                            </b-button>
                        </template>
                        <template v-else>
                            <b-button 
                                variant="outline-primary" 
                                size="sm" 
                                @click="dirModal.show = true"
                                v-if="$can('create_dir protokol-media')"
                                class="mr-2"
                            >
                                <b-icon icon="folder-plus"></b-icon>
                                {{ $t('newDirectory') }}
                            </b-button>
                            <b-button 
                                variant="outline-danger" 
                                size="sm" 
                                @click="deleteDir"
                                v-if="this.currentDir != '' && $can('delete_dir protokol-media')"
                            >
                                <b-icon icon="folder-x"></b-icon>
                                {{ $t('deleteDirectory') }}
                            </b-button>
                        </template>
                    </div>
                </div>

                <!-- Directories Grid -->
                <div v-if="dirs && dirs.length > 0" class="directories-section">
                    <div :class="['directories-grid', `view-${viewMode}`]">
                        <div 
                            v-for="item in dirs" 
                            :key="item.name" 
                            class="directory-card"
                            @click="chdir(item.name)"
                        >
                            <div class="directory-icon">
                                <b-icon :icon="isDirSublib(item.name) ? 'cloud-arrow-down' : 'folder-fill'" font-scale="2"></b-icon>
                            </div>
                            <div class="directory-info">
                                <span class="directory-name">{{ item.name }}</span>
                                <span v-if="isDirSublib(item.name)" class="directory-badge">
                                    <b-badge variant="info">Sub-library</b-badge>
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Files Grid/List -->
                <div v-if="items !== undefined" :class="['files-section', `view-${viewMode}`]">
                    <File 
                        v-for="item in files" 
                        :key="item.key"
                        :file="item" 
                        :library="currentLib"
                        :itemsSelected="itemsSelected" 
                        @selectFile="selectFile" 
                        :public="item.public"
                        :size="fileSize" 
                        :currentPath="currentDir"
                        :viewMode="viewMode"
                        @showContextMenu="handleShowContextMenu"
                        @fileConverted="handleFileConverted"
                        @fileMoved="handleFileMoved"
                        @deleteFile="handleContextDeleteFile"
                        ref="fileComponents"
                    />
                </div>

                <scroll-loader :loader-method="getMoreMedia" :loader-disable="disableLoader"></scroll-loader>
            </div>

                <UploadFileModal
                    :uploadModal="uploadModal"
                    :currentLib="currentLib"
                    :currentDir="currentDir"
                />

                <main-dialog :title="$t('newDirectory')" v-model="dirModal.show" :ok-title="$t('create')"
                    ok-variant="success" @ok="createDir">
                    <InputField name="name" :label="this.$t('name')" v-model="dirModal.name" @error="fieldError"
                        :rules="{ required: true, length: { min: 2 } }" />
                </main-dialog>

            <main-dialog :title="$t('moveToDirectory')" v-model="moveModal.show" :ok-title="$t('move')"
                ok-variant="success" @ok="moveFiles">
                <SelectField name="dir" :label="this.$t('directory')" v-model="moveModal.dir" @error="fieldError"
                    :items="deepDirs" :rules="{ required: true }" />
            </main-dialog>
        </div>

        <b-skeleton v-else width="100%" height="250px" animation="throb" />

        <b-sidebar v-model="createLibrary.show" right backdrop>
            <div class="px-3 py-2">
                <InputField name="name" :label="this.$t('name')" v-model="createLibrary.data.name" @error="fieldError"
                    :rules="{ required: true, length: { min: 2 } }" />

                <template v-if="!fieldHasError('name')">

                    <SelectField name="provider" :label="this.$t('provider')"
                        :description="this.$t('chooseMediaProvider')" v-model="createLibrary.data.provider"
                        :items="libProviders" :reduce="(o) => o" :rules="{ required: true }" />
                </template>

                <!-- <div v-if="createLibrary.data.provider == 'protokol-simple'">
                {{  $t('integrations.media.protokolSimpleDescription')  }}
            </div> -->

                <template v-if="createLibrary.data.provider == 's3'">
                    <InputField name="endpoint" :label="this.$t('endpoint')" v-model="createLibrary.data.s3_endpoint"
                        @error="fieldError" :rules="{ required: true, length: { min: 2 } }" />

                    <InputField name="region" :label="this.$t('region')" v-model="createLibrary.data.s3_region"
                        @error="fieldError" :rules="{ required: true, length: { min: 2 } }" />

                    <InputField name="bucket" :label="this.$t('bucket')" v-model="createLibrary.data.s3_bucket"
                        @error="fieldError" :rules="{ required: true, length: { min: 2 } }" />

                    <InputField name="key" :label="this.$t('key')" v-model="createLibrary.data.s3_key"
                        @error="fieldError" :rules="{ required: true, length: { min: 2 } }" />

                    <InputField type="password" name="secret" :label="this.$t('secret')"
                        v-model="createLibrary.data.s3_secret" @error="fieldError"
                        :rules="{ required: true, length: { min: 2 } }" />

                    <InputField name="host" :label="this.$t('host')" v-model="createLibrary.data.host"
                        @error="fieldError" :description="this.$t('libraryPublicUrl')"
                        :rules="{ required: true, placeholder: 'https://library.example.com', length: { min: 2 } }" />

                    <InputField name="dir" :label="this.$t('rootDirMedia')" v-model="createLibrary.data.dir"
                        @error="fieldError" :description="this.$t('rootDirMedia')"
                        :rules="{ required: false, length: { min: 2 } }" />
                </template>
            </div>
            <template #footer>
                <div class="d-flex bg-dark text-light align-items-center px-3 py-2">
                    <b-button size="sm" variant="success" @click="createLibraryAction" v-if="canShowCreate">{{
                        $t('create') }}</b-button>
                    <protokol-checkout-button size="sm" v-if="canShowBuy" productType="integration"
                        reference="protokol-media">{{ $t('buy') }}</protokol-checkout-button>
                </div>
            </template>
        </b-sidebar>
    </div>
</template>

<style scoped lang="scss">
@import '@/assets/scss/_custom.scss';

.media-library-container {
    padding: 24px;
    min-height: 100vh;
}

.empty-library-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 80px 20px;
    background: $dark-background;
    border: 2px dashed $dark-border2;
    border-radius: 16px;
    text-align: center;

    .empty-icon {
        color: var(--text-muted);
        margin-bottom: 24px;
        opacity: 0.5;
    }

    h4 {
        color: var(--text-primary);
        font-weight: 600;
        margin-bottom: 12px;
    }
}

.media-library-content {
    display: flex;
    flex-direction: column;
    gap: 20px;
}

.media-header {
    background: $dark-background;
    border: 1px solid $dark-border2;
    border-radius: 12px;
    padding: 24px;
    margin-bottom: 20px;

    .header-top {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 20px;

        .library-title {
            font-size: 1.5rem;
            font-weight: 700;
            color: var(--text-primary);
            margin: 0;
            display: flex;
            align-items: center;
        }

        .header-actions {
            display: flex;
            align-items: center;
            gap: 12px;

            .view-toggle {
                ::v-deep button {
                    padding: 8px 12px;
                }
            }
        }
    }

    .header-search-breadcrumb {
        display: flex;
        gap: 20px;
        align-items: center;
        flex-wrap: wrap;

        .breadcrumb-nav {
            flex: 1;
            display: flex;
            align-items: center;
            gap: 8px;
            min-width: 200px;
            padding: 12px 16px;
            background: $dark-hard2-background;
            border-radius: 8px;
            border: 1px solid $dark-border2;

            .breadcrumb-item {
                color: $protokol-royal-active;
                text-decoration: none;
                padding: 0 4px;
                font-weight: 500;
                transition: all 0.2s;

                &:hover {
                    color: #7d92f5;
                    background: rgba(102, 126, 234, 0.1);
                    border-radius: 4px;
                }

                &.active {
                    color: var(--text-primary);
                    pointer-events: none;
                }
            }

            .breadcrumb-separator {
                color: var(--text-muted);
                font-size: 0.9rem;
            }
        }

        .search-box {
            position: relative;
            min-width: 280px;

            .search-icon {
                position: absolute;
                left: 14px;
                top: 50%;
                transform: translateY(-50%);
                color: var(--text-muted);
                pointer-events: none;
            }

            .search-input {
                padding-left: 40px;
                background: $dark-hard2-background;
                border: 1px solid $dark-border2;
                color: var(--text-primary);
                border-radius: 8px;
                height: 42px;

                &:focus {
                    background: $dark-hard2-background;
                    border-color: $protokol-royal-active;
                    color: var(--text-primary);
                    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
                }

                &::placeholder {
                    color: var(--text-muted);
                }
            }
        }
    }
}

.loading-spinner {
    display: flex;
    justify-content: center;
    padding: 60px;
}

.media-toolbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 20px;
    background: $dark-background;
    border: 1px solid $dark-border2;
    border-radius: 12px;
    margin-bottom: 20px;

    .toolbar-left {
        .selection-count {
            color: var(--text-primary);
            font-weight: 600;
            padding: 8px 16px;
            background: rgba(102, 126, 234, 0.15);
            border-radius: 6px;
            font-size: 0.9rem;
        }
    }

    .toolbar-right {
        display: flex;
        gap: 8px;
    }
}

.drop-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.85);
    backdrop-filter: blur(4px);
    z-index: 9999;
    display: flex;
    align-items: center;
    justify-content: center;
    animation: fadeIn 0.2s ease;

    .drop-overlay-content {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 20px;
        padding: 60px;
        background: $dark-background;
        border: 3px dashed $protokol-royal-active;
        border-radius: 20px;
        animation: scaleIn 0.3s ease;

        .drop-icon {
            color: $protokol-royal-active;
            animation: pulse 1.5s ease-in-out infinite;
        }

        .drop-text {
            color: var(--text-primary);
            font-size: 1.5rem;
            font-weight: 700;
            margin: 0;
            text-align: center;
        }
    }
}

@keyframes fadeIn {
    from {
        opacity: 0;
    }
    to {
        opacity: 1;
    }
}

@keyframes scaleIn {
    from {
        transform: scale(0.9);
        opacity: 0;
    }
    to {
        transform: scale(1);
        opacity: 1;
    }
}

@keyframes pulse {
    0%, 100% {
        transform: scale(1);
        opacity: 0.8;
    }
    50% {
        transform: scale(1.1);
        opacity: 1;
    }
}

.directories-section {
    margin-bottom: 32px;
}

.directories-grid {
    display: grid;
    gap: 16px;
    
    &.view-grid {
        grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    }

    &.view-list {
        grid-template-columns: 1fr;
    }

    .directory-card {
        display: flex;
        align-items: center;
        gap: 16px;
        padding: 20px;
        background: $dark-background;
        border: 1px solid $dark-border2;
        border-radius: 12px;
        cursor: pointer;
        transition: all 0.2s ease;

        &:hover {
            border-color: $protokol-royal-active;
            background: rgba(102, 126, 234, 0.05);
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
        }

        .directory-icon {
            width: 48px;
            height: 48px;
            display: flex;
            align-items: center;
            justify-content: center;
            background: rgba(102, 126, 234, 0.15);
            border-radius: 10px;
            color: $protokol-royal-active;
            flex-shrink: 0;
        }

        .directory-info {
            flex: 1;
            display: flex;
            flex-direction: column;
            gap: 4px;
            min-width: 0;

            .directory-name {
                font-weight: 600;
                color: var(--text-primary);
                font-size: 1rem;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }

            .directory-badge {
                font-size: 0.75rem;
            }
        }
    }
}

.files-section {
    display: grid;
    gap: 16px;
    
    &.view-grid {
        grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
    }

    &.view-list {
        grid-template-columns: 1fr;
    }
}

@media (max-width: 768px) {
    .media-header {
        .header-top {
            flex-direction: column;
            align-items: stretch;
            gap: 16px;
        }

        .header-search-breadcrumb {
            flex-direction: column;
            
            .breadcrumb-nav,
            .search-box {
                width: 100%;
                min-width: 100%;
            }
        }
    }

    .media-toolbar {
        flex-direction: column;
        gap: 12px;
        align-items: stretch;

        .toolbar-left,
        .toolbar-right {
            width: 100%;
            justify-content: center;
        }

        .toolbar-right {
            display: flex;
            gap: 8px;
        }
    }

    .directories-grid {
        &.view-grid {
            grid-template-columns: 1fr;
        }
    }

    .files-section {
        &.view-grid {
            grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
        }
    }
}
</style>
<script>
import Input from "@/components/form/Input"
import Select from "@/components/form/Select"
import Page500 from "../../../../views/pages/Page500"
import HelptipPopup from "../../../elements/HelptipPopup"
import _ from "lodash"
import File from "./File"
import { Switch as cSwitch } from '@coreui/vue'
import UploadFileModal from "./UploadFileModal.vue";
//import DateTime from "@/components/form/DateTime"

export default {
    name: "Media",
    props: {
        mode: {
            type: String,
            default: 'library'
        }
    },

    components: {
        InputField: Input,
        SelectField: Select,
        ErrorPage: Page500,
        File,
        HelptipPopup,
        cSwitch,
        //DateTimeField: DateTime
        UploadFileModal
    },
    data: function () {
        return {
            libraryUnavailable: false,
            ready: false,
            noPermissions: false,
            changingLib: false,
            errors: [],
            dirPath: [],
            itemsSelected: [],
            keyword: '',
            viewMode: 'grid', // 'grid' or 'list'
            isDragging: false,
            dragCounter: 0,
            uploadModal: {
                show: false,
                files: [],
                metadata: [],
                expiresAt: null
            },
            dirModal: {
                show: false,
                name: ''
            },
            moveModal: {
                show: false,
                dir: ''
            },
            storage: {
                allowedSpace: 0,
                freeSpace: 0
            },
            editLibrary: {
                show: false,
                data: null
            },
            createLibrary: {
                show: false,
                data: {
                    name: ''
                }
            },
            share: {
                types: ['link', 'email'],
                type: 'link',
                url: null,
                emails: '',
                validUntil: '',
                mimeType: 'application/octet-stream',
                key: ''
            },

            dirs: [],

            searchedItems: null,
            lastKey: null,
            disableLoader: false,
            limit: 12
        }
    },
    watch: {
        "fileModal.data.orig_name": function (nVal, oVal) {
            this.fileModal.data.orig_name = nVal.replace(" ", "_");
        },
        "dirModal.name": function (nVal, oVal) {
            this.dirModal.name = nVal.replace("/", "");
        },
    },
    computed: {
        items() {
            if (this.searchedItems) {
                return this.searchedItems
            }

            return this.$store.getters.media(this.currentDir)
        },

        files() {
            if (this.items) {
                let items = this.items.filter(f => {
                    return f.mime_type != "dir"
                })

                return items
            }
        },

        deepDirs() {
            return this.$store.getters.dirs.filter(d => {
                if (d == ".") {
                    return d != this.currentDir
                } else {
                    return "/" + d != this.currentDir
                }

            })
        },

        currentDir() {
            return this.dirPath.join("/")
        },

        prevDir() {
            return this.dirPath[this.dirPath.length - 2]
        },

        dirSize() {
            if (this.mode == "library") return 3
            return 4
        },

        fileSize() {
            if (this.mode == "library") return 2
            return 3
        },

        getOccupiedSpace() {
            let lib = this.$store.getters.library
            if (lib !== null) {
                let l = (lib.allowed_storage / (lib.allowed_storage - lib.size)) / 100

                return l.toFixed(2)
            }
        },

        freeSpace() {
            return 100 - this.getOccupiedSpace
        },

        libraries: {
            set(val) {
                this.$store.commit("SET_LIBRARIES", val)
            },

            get() {
                return this.$store.getters.libraries
            }
        },

        currentLib: {
            set(val) {
                if (this.mode != "library") {
                    this.$store.commit("SET_LIBRARY", val)
                    return
                }

                this.$router.replace({
                    query: {
                        lib: val.uuid
                    }
                });

                this.$store.commit("SET_LIBRARY", val)
            },

            get() {
                if (this.mode == "standalone") {
                    return this.libraries[0]
                }

                const [ library ] = this.libraries || []
                if (!library) {
                    return null
                }

                let lib = library.uuid // this.$route.query.lib;
                if (lib === undefined) {
                    return this.libraries[0]
                }

                return this.libraries.find(l => {
                    return l.uuid == lib
                })
            }
        },

        libProviders() {
            let providers = [
                'protokol-simple'
            ]

            const libs = this.libraries || []
            const exist = libs.find(l => {
                return l.provider == 'protokol-simple'
            })

            if (exist) {
                providers.pop()
            }

            return providers
        },

        canCreateLibFromDir() {
            if (this.currentLib.dir.substring(0, 1) == "$") {
                return false
            }

            if (this.currentDir == "") {
                return false
            }

            let find = this.libraries.find(l => {
                return l.dir == "$/" + this.currentLib.uuid + this.currentDir
            })

            if (find !== undefined) {
                return false
            }

            return true
        },

        currentLibName() {
            if (this.currentLib) {
                if (this.currentLib.name != '') {
                    return this.truncate(this.currentLib.name, { length: 15 })
                }

                return this.$t('default')
            }
        },

        canShowBuy() {
            if (this.createLibrary.data.provider == 'protokol-simple') {
                let integration = this.$store.getters.getIntegrationByID('protokol-media')
                let subs = this.$store.getters.getSubscriptions || []

                let sub = subs.find(s => {
                    return s.product_id == integration.template.product_id
                })

                if (sub) {
                    return false
                }

                return true
            }

            return false
        },

        canShowCreate() {
            if (this.canShowBuy) return false

            let libs = this.libraries || []
            return libs.find(l => {
                return l.provider == 'protokol-simple'
            }) === undefined
        },
    },
    methods: {
        fieldHasError(field) {
            const res = this.errors.find(f => {
                return f.field == field && f.error
            }) !== undefined

            return res
        },

        truncate: _.truncate,

        getMoreMedia() {
            this.$store.dispatch("fetchMedia", { path: this.currentDir, uuid: this.currentLib.uuid, last_key: this.lastKey, limit: this.limit }).then((data) => {
                //this.chdir(this.currentDir)

                const lastItem = data.pop()
                if (lastItem)
                    this.lastKey = lastItem.key

                if ((data.length + 1) < this.limit) this.disableLoader = true

            }).catch(error => {
                if (error.response !== undefined) {
                    if (error.response.status == 500) {
                        this.$serverErrorModal(error)
                        return
                    }
                }

                console.log(error.message);
            })
        },

        chdir(dir) {
            this.dirs = []
            this.lastKey = null
            this.searchedItems = null
            let index = this.dirPath.indexOf(dir)

            if (index == -1) {
                this.dirPath.push(dir)
            } else {
                this.dirPath = this.dirPath.slice(0, index + 1)
            }

            this.itemsSelected = []

            if (this.mode == "library") {
                history.pushState(null, null, '#' + this.currentDir);
            }

            const lastItemInPath = this.$store.getters.getLastItemInDir(this.currentDir)
            if (lastItemInPath) {
                this.lastKey = lastItemInPath.key
            }

            this.disableLoader = false

            this.$store.dispatch("fetchDirs", { path: this.currentDir, uuid: this.currentLib.uuid }).then(resp => {
                this.dirs = resp
            })
        },

        shareUpload(e) {
            if (this.share.type == 'link')
                e.preventDefault();

            this.$media.share(this.currentLib.uuid, { key: this.currentDir + "/" + this.share.key, mimeType: this.share.mimeType, date: this.share.validUntil }).then(resp => {
                //console.log(resp)
                this.share.url = resp.data
                this.share.validUntil = ''
                this.share.key = ''
            }).catch(error => {
                if (error.response !== undefined) {
                    if (error.response.status == 500) {
                        this.$serverErrorModal(error)
                        return
                    }

                    this.$mainDialog.msgBoxOk(error.response.message)
                }
            })
        },

        downloadFile() {
            let item = this.itemsSelected[0]
            this.$media.download(this.currentLib.uuid, item.key).then(resp => {
                const url = window.URL.createObjectURL(new Blob([resp.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', item.name); //or any other extension
                document.body.appendChild(link);
                link.click();
            }).catch(err => {
                console.log(err)
            })
        },

        createDir() {
            if (this.checkForErrors()) {
                this.$mainDialog.msgBoxOk(this.$t("someFieldsAreNotValid"))

                return
            }

            this.$media.createDir(this.currentLib.uuid, this.currentDir + "/" + this.dirModal.name).then(resp => {
                this.dirs.push({
                    mime_type: 'dir',
                    name: this.dirModal.name
                })

                this.dirModal.name = ''
            }).catch(err => {
                if (err.response === undefined) {
                    this.$mainDialog.msgBoxOk(err.message)
                    return
                }

                if (err.response.status == 500) {
                    this.$serverErrorModal(err)
                    return
                }

                this.dirModal.name = ''

                this.$mainDialog.msgBoxOk(err.response.data.message)
            })
        },

        deleteDir() {
            this.$mainDialog.msgBoxConfirm(this.$t("actionWillRemoveDirAndContents")).then(resp => {
                if (resp) {
                    let dirToDelete = _.trimStart(this.currentDir, "/")

                    this.$media.deleteDir(this.currentLib.uuid, dirToDelete).then(resp => {
                        this.$store.commit("REMOVE_DIR", this.currentDir)
                        this.chdir(this.prevDir)
                        // refresh library info to check for available storage
                        //this.$store.dispatch("fetchLibraryInfo")
                    }).catch(err => {
                        if (err.response === undefined) {
                            this.$mainDialog.msgBoxOk(err.message)
                            return
                        }

                        if (err.response.status == 500) {
                            this.$serverErrorModal(err)
                            return
                        }

                        this.$mainDialog.msgBoxOk(err.response.message)
                    })
                }
            })

        },

        handleGlobalDragEnter(e) {
            // Only activate if dragging files from outside
            if (e.dataTransfer.types.includes('Files')) {
                this.dragCounter++
                this.isDragging = true
            }
        },

        handleGlobalDragLeave(e) {
            this.dragCounter--
            if (this.dragCounter === 0) {
                this.isDragging = false
            }
        },

        handleGlobalDragOver(e) {
            if (e.dataTransfer.types.includes('Files')) {
                e.dataTransfer.dropEffect = 'copy'
            }
        },

        handleGlobalDrop(e) {
            this.isDragging = false
            this.dragCounter = 0

            const files = Array.from(e.dataTransfer.files)
            if (files.length > 0) {
                this.uploadModal.files = files
                this.uploadModal.show = true
            }
        },

        isDirSublib(dir) {
            let find = this.libraries.find(l => {
                return l.dir == "$/" + this.currentLib.uuid + this.currentDir + "/" + dir
            })

            if (find !== undefined) {
                return true
            }

            return false
        },

        selectFile(e, item) {
            if (e.ctrlKey || e.metaKey) {
                let find = this.itemsSelected.find(id => {
                    return item.uuid == id
                })

                if (find === undefined) {
                    this.itemsSelected.push(item)
                } else {
                    this.itemsSelected = this.itemsSelected.filter(id => {
                        return item.uuid != id
                    })
                }
            } else {
                this.itemsSelected = [item]
            }

            this.$emit("itemsSelected", this.itemsSelected)
        },

        handleShowContextMenu(fileKey) {
            // Close all other context menus
            if (this.$refs.fileComponents) {
                this.$refs.fileComponents.forEach(fileComponent => {
                    if (fileComponent.file.key !== fileKey) {
                        fileComponent.closeContextMenu();
                    }
                });
            }
        },

        handleFileConverted() {
            // Refresh the media list to show the new converted file
            this.$store.dispatch("fetchMedia", { path: this.currentDir, uuid: this.currentLib.uuid });
        },

        handleFileMoved(data) {
            // Remove the moved file from the current view
            this.$store.commit("REMOVE_MEDIA", data.file);
            
            // Refresh the media list
            this.$store.dispatch("fetchMedia", { path: this.currentDir, uuid: this.currentLib.uuid });
        },

        editFile() {
            let item = this.itemsSelected[0]
            if (item !== undefined) {
                let file = this.$store.getters.mediaFile(this.currentDir, this.itemsSelected[0])

                this.fileModal.title = file.orig_name
                this.fileModal.data = file
                this.fileModal.show = true
            }
        },

        deleteFile() {
            this.$mainDialog.msgBoxConfirm(this.$t("selectedFilesWillBeDeleted")).then(resp => {
                if (resp) {
                    let popup = this.$loadingPopup(this.$t('deleting') + "...")

                    this.$media.delete({ ref: this.currentLib.uuid, items: this.itemsSelected }).then((resp) => {
                        this.itemsSelected = []

                        resp.data.forEach(d => {
                            this.$store.commit("REMOVE_MEDIA", d)
                        })
                    }).catch(error => {
                        if (error.response !== undefined) {
                            if (error.response.status == 500) {
                                this.$serverErrorModal(error)
                                return
                            }

                            this.$mainDialog.msgBoxOk(error.response.message)
                        }

                        throw error

                    }).finally(() => {
                        popup.hide()
                    })
                }
            })
        },

        handleContextDeleteFile(file) {
            let popup = this.$loadingPopup(this.$t('deleting') + "...")

            this.$media.delete({ ref: this.currentLib.uuid, items: [file] }).then((resp) => {
                this.itemsSelected = this.itemsSelected.filter(i => i.key !== file.key)

                resp.data.forEach(d => {
                    this.$store.commit("REMOVE_MEDIA", d)
                })
            }).catch(error => {
                if (error.response !== undefined) {
                    if (error.response.status == 500) {
                        this.$serverErrorModal(error)
                        return
                    }

                    this.$mainDialog.msgBoxOk(error.response.message)
                }
            }).finally(() => {
                popup.hide()
            })
        },

        moveFiles() {
            this.$mainDialog.msgBoxConfirm($t('filesWillBeMovedToSelectedDir')).then(resp => {
                if (resp) {
                    let moveToDir = this.moveModal.dir
                    if (moveToDir == ".") {
                        moveToDir = ""
                    }

                    if (moveToDir != "") {
                        moveToDir = "/" + moveToDir
                    }

                    this.$mediaHttp.post("/media/move", { path: moveToDir, items: this.itemsSelected }).then(resp => {
                        this.$store.commit("MOVE_MEDIA", { from: this.currentDir, to: moveToDir, items: this.itemsSelected })
                    })
                }
            })
        },

        search: _.debounce(function (e) {
            if (e == "") {
                this.searchedItems = null
                this.$store.dispatch("fetchMedia", { path: this.currentDir, uuid: lib }).catch(err => {
                    this.$mainDialog.msgBoxOk(err.message, { noStacking: true })
                    this.chdir('')
                }).catch(error => {
                    if (error.response !== undefined) {
                        if (error.response.status == 500) {
                            this.$serverErrorModal(error)
                            return
                        }

                        return
                    }
                })

                return
            }

            this.$store.dispatch("searchMedia", { path: this.currentDir, uuid: this.currentLib.uuid, keyword: e })
                .then(resp => {
                    this.searchedItems = resp
                })
                .catch(error => {
                    if (error.response !== undefined) {
                        if (error.response.status == 500) {
                            this.$serverErrorModal(error)
                            return
                        }
                    }

                    this.$debugLog(error.message);
                })
        }, 500),


        isSelected(uuid) {
            return this.itemsSelected.indexOf(uuid) !== -1
        },

        fieldError(error) {
            let found = this.errors.find((i, e) => {
                return i.field == error.field
            })

            if (found === undefined) {
                this.errors.push(error)
            } else {
                this.errors.map(e => {
                    if (found.field == e.field)
                        e.error = error.error
                })
            }
        },

        checkForErrors() {
            return this.errors.find((i, e) => {
                return i.error
            })
        },

        changeLib(lib) {
            this.changingLib = true
            this.$store.commit("CLEAN_MEDIA")
            this.currentLib = lib
            this.chdir('')
            this.lastKey = null

            this.$store.dispatch("fetchLibraryInfo", lib.uuid).then(data => {
                this.storage.allowedSpace = data.allowed_storage
                this.storage.size = data.size
                this.storage.freeSPace = data.allowed_storage - data.size

                this.ready = true
                this.$store.dispatch("fetchMedia", { path: this.currentDir, uuid: lib.uuid })
                    .then(() => {
                        try {
                            this.$router.replace({ query: { lib: lib.uuid } })
                        } catch {
                            // do nothing
                        }

                        this.changingLib = false
                    })
                    .catch(error => {
                        this.$mainDialog.msgBoxOk(error.message, { noStacking: true })
                        this.chdir('')

                        if (error.response !== undefined) {
                            if (error.response.status == 500) {
                                this.$serverErrorModal(error)
                                return
                            }

                            return
                        }
                    })
            }).catch(error => {
                if (error.response !== undefined) {
                    if (error.response.status == 500) {
                        this.$serverErrorModal(error)
                        return
                    }

                    return
                }

            })


        },

        createLibraryAction() {
            this.$mediaHttp.post("/v1/dms/media/library", this.createLibrary.data).then(resp => {
                this.$bvToast.toast(this.$t('libraryCreated'), {
                    variant: 'success',
                    title: this.$t('success')
                })

                this.$store.commit("ADD_LIBRARY", resp.data)

                this.createLibrary.show = false
                this.createLibrary.data = {}
            }).catch(error => {
                this.$debugLog(error)
                if (error.response !== undefined) {
                    if (error.response.status == 500) {
                        this.$serverErrorModal(error)
                        return
                    }

                    this.$mainDialog.msgBoxOk(error.response.data.message)

                    return
                }
            })
        },

        createLibraryFromDirAction() {
            let data = {
                name: this.dirPath[this.dirPath.length - 1],
                provider: this.currentLib.provider,
                dir: this.currentDir,
            }

            this.$mediaHttp.post("/v1/dms/media/library", data).then(resp => {

                this.$bvToast.toast(this.$t('libraryCreated'), {
                    variant: 'success',
                    title: "Success"
                })

                this.$store.commit("ADD_LIBRARY", resp.data)

                this.createLibrary.show = false
                this.createLibrary.data = {}
            }).catch(error => {
                if (error.response !== undefined) {
                    if (error.response.status == 500) {
                        this.$serverErrorModal(error)
                        return
                    }

                    this.$mainDialog.msgBoxOk(error.response.data.message)

                    return
                }
            })
        },

        editLib(lib) {
            this.editLibrary.show = true
            this.editLibrary.data = lib
        },

        isProtokolProvider() {
            return this.currentLib.provider == ""
        },

        isLibSelected(lib) {
            if (this.currentLib) {
                return this.currentLib.uuid == lib.uuid
            }

            return false
        },

        buy() {
            const popup = this.$loadingPopup()

            this.$transactionApi.post("/checkout/link", {
                product_type: "integration",
                reference: "protokol-media",
                lang: this.$store.getters.getLocale,
                source: `platform`
            }).then(resp => {
                window.open(resp.data, '_blank')
            }).catch(err => {
                this.processInfo = false

                if (err.response) {
                    if (err.response.status == 501) {
                        this.$bvToast.toast(this.$t('checkoutMethodCurrentlyDisabled'), {
                            title: this.$t('error'),
                            variant: 'danger'
                        })
                        return
                    }
                }

                this.$mainDialog.msgBoxOk(this.$t('failedToCheckout'))
            }).finally(() => {
                popup.hide()
            })
        },
    },
    mounted() {
        if (!this.$can("use protokol-media")) {
            this.noPermissions = true
            return
        }

        this.dirPath = [""]
        if (this.mode == "library") {
            let dirpath = window.location.href.split('#')[1]
            if (dirpath !== undefined) {
                this.dirPath = dirpath.split("/")
            }
        }

        this.$store.dispatch("fetchLibraries").then((libs) => {
            if (!libs || libs.length === 0) {
                return
            }

            let lib = this.$route.query.lib || libs[0].uuid
            if (this.mode == "standalone") {
                lib = libs[0].uuid
            }

            if (lib !== undefined && libs.length > 0) {
                this.$store.dispatch("fetchDirs", { path: this.currentDir, uuid: lib }).then(resp => {
                    this.dirs = resp
                })

                this.$store.dispatch("fetchLibraryInfo", libs[0].uuid).then(data => {
                    this.storage.allowedSpace = data.allowed_storage
                    this.storage.size = data.size
                    this.storage.freeSPace = data.allowed_storage - data.size

                    this.ready = true
                }).catch(error => {
                    if (error.response !== undefined) {
                        if (error.response.status == 500) {
                            this.$serverErrorModal(error)
                            return
                        }

                        this.libraryUnavailable = true
                        return
                    }

                    if (!error.status) {
                        this.libraryUnavailable = true
                    }

                })
            }


        }).catch(err => {
            this.libraryUnavailable = true
            this.$debugLog(err)
        })

        let checkoutPage = new BroadcastChannel('checkout')
        checkoutPage.onmessage = (msg) => {
            if (msg.data.type == "checkout_finished") {
                this.createLibraryAction()
            }
        }
    }
}
</script>
